import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import FormBuilder from '../../../../../../shared/components/form-builder/form-builder';
import { IAdditionalInfo } from '../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto';
import { useTransactionCertificateStore } from '../../../../../../store/transaction-certificates.store';

const FABRIC_WEIGHT_CONSTRUCT_PC_CODES = ['PC0025', 'PC0026', 'PC0027', 'PC0028', 'PC0039'];
const YARN_COUNT_PC_CODES = ['PC0029', 'PC0030', 'PC0031'];
const FIBER_LENGTH_PC_CODES = ['PC0032', 'PC0033', 'PC0034'];
const FABRIC_WEIGHT_FINISHED_PC_CODES = [
  'PC0001','PC0002','PC0003','PC0004','PC0005','PC0006','PC0007','PC0008','PC0009','PC0010',
  'PC0015','PC0018','PC0019','PC0020','PC0021',
];
const FABRIC_WEIGHT_FINISHED_PD_CODES = [
  'PD0001','PD0002','PD0003','PD0004','PD0005','PD0006','PD0007','PD0008','PD0009','PD0010',
  'PD0011','PD0012','PD0013','PD0014','PD0015','PD0016','PD0017',
  'PD0019','PD0020','PD0021','PD0022','PD0023','PD0024',
  'PD0026','PD0028','PD0029','PD0042','PD0043','PD0044','PD0051',
];

interface AdditionalInfoProps {
  additionalInfoData: IAdditionalInfo;
  setAdditionalInfoData: React.Dispatch<React.SetStateAction<IAdditionalInfo>>;
  formRef: React.Ref<any>;
  productCategoryCode?: string;
  productDetailCode?: string;
  /** When false (product without component), Product Additives Weight is in stepper 1 only – hide it here */
  addWithComponent?: boolean;
}

const AdditionalInfo: React.FC<AdditionalInfoProps> = ({
  additionalInfoData,
  setAdditionalInfoData,
  formRef,
  productCategoryCode = '',
  productDetailCode = '',
  addWithComponent = true,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { getTCStandardGOTS } = useTransactionCertificateStore();

  const toggleDrawer = () => {
    if (drawerOpen) resetForm();
    setDrawerOpen((prev) => !prev);
  };

  const resetForm = () => {
    setAdditionalInfoData(additionalInfoData);
  };

  const formConfig = {
    pageName: "certified-products",
    subPageName: "tcProductAdditionalInfo",
    initialValues: additionalInfoData,
    validationSchema: Yup.object().shape({
      tcProductInfoFiberLength: Yup.string()
        .nullable()
        .when([], {
          is: () => !addWithComponent && FIBER_LENGTH_PC_CODES.includes(productCategoryCode),
          then: Yup.string().required("Fiber Length is required for the selected product category"),
          otherwise: Yup.string().nullable(),
        })
        .test(
          "no-invalid-values",
          "Invalid value. Please enter a valid fiber length or leave blank.",
          (value: string | null | undefined) => {
            if (!value || value.trim() === "") return true; // Allow empty/blank
            const invalidValues = ["n/a", "na", "N/A", "NA", "NULL", "Null", "null", "Not available", "NOT AVAILABLE", "not available"];
            return !invalidValues.includes(value.trim());
          }
        )
        .max(255, "Maximum 255 Characters is allowed"),
      tcProductInfoYarnCount: Yup.string()
        .nullable()
        .when([], {
          is: () => !addWithComponent && YARN_COUNT_PC_CODES.includes(productCategoryCode),
          then: Yup.string().required("Yarn Count is required for the selected product category"),
          otherwise: Yup.string().nullable(),
        })
        .test(
          "no-invalid-values",
          "Invalid value. Please enter a valid yarn count or leave blank.",
          (value: string | null | undefined) => {
            if (!value || value.trim() === "") return true; // Allow empty/blank
            const invalidValues = ["n/a", "na", "N/A", "NA", "NULL", "Null", "null", "Not available", "NOT AVAILABLE", "not available"];
            return !invalidValues.includes(value.trim());
          }
        )
        .max(255, "Maximum 255 Characters is allowed"),
      tcProductInfoFabricWeightAndConstruct: Yup.string()
        .nullable()
        .when([], {
          is: () => !addWithComponent && FABRIC_WEIGHT_CONSTRUCT_PC_CODES.includes(productCategoryCode),
          then: Yup.string().required("Fabric Weight and Construct is required for the selected product category"),
          otherwise: Yup.string().nullable(),
        })
        .test(
          "no-invalid-values",
          "Invalid value. Please enter a valid fabric weight and construct or leave blank.",
          (value: string | null | undefined) => {
            if (!value || value.trim() === "") return true; // Allow empty/blank
            const invalidValues = ["n/a", "N/A", "na", "NA", "NULL", "Null", "null", "Not available", "NOT AVAILABLE", "not available"];
            return !invalidValues.includes(value.trim());
          }
        )
        .max(255, "Maximum 255 Characters is allowed"),
      tcProductInfoFabricWeightOfFinishedProduct: Yup.string()
        .nullable()
        .when([], {
          is: () => !addWithComponent && (FABRIC_WEIGHT_FINISHED_PC_CODES.includes(productCategoryCode) || FABRIC_WEIGHT_FINISHED_PD_CODES.includes(productDetailCode)),
          then: Yup.string().required("Fabric Weight of Finished Product is required for the selected product category/detail"),
          otherwise: Yup.string().nullable(),
        })
        .test(
          "no-invalid-values",
          "Invalid value. Please enter a valid fabric weight or leave blank.",
          (value: string | null | undefined) => {
            if (!value || value.trim() === "") return true; // Allow empty/blank
            const invalidValues = ["n/a", "na", "N/A", "NA", "NULL", "Null", "null", "Not available", "NOT AVAILABLE", "not available"];
            return !invalidValues.includes(value.trim());
          }
        )
        .max(255, "Maximum 255 Characters is allowed"),
      tcProductInfoTrimsExcluded: Yup.string().nullable().max(255, "Maximum 255 Characters is allowed"),
      tcProductInfoAdditional: Yup.string().nullable(),
      tcProductAdditivesWeight: Yup.string()
        .nullable()
        .test(
          "is-exactly-five-decimal-places",
          "Number must be a valid decimal number with up to 5 decimal places.",
          (value: string | null | undefined) => {
            if (!value) return true;
            return /^(0|[0-9]\d*|0\.\d{1,5}|[0-9]\d*\.\d{1,5})$/.test(value);
          }
        )
  
        .max(255, "Maximum 255 Characters is allowed"),
    }),
    sections: [
      {
        title: 'Additional Information',
        fields: [
          {
            label: 'Fiber Length (mm)',
            name: 'tcProductInfoFiberLength',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: !addWithComponent && FIBER_LENGTH_PC_CODES.includes(productCategoryCode),
            value: additionalInfoData.tcProductInfoFiberLength,
            props: {
              placeholder: 'Enter Fiber Length',
            },
          },
          {
            label: 'Yarn Count',
            name: 'tcProductInfoYarnCount',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: !addWithComponent && YARN_COUNT_PC_CODES.includes(productCategoryCode),
            value: additionalInfoData.tcProductInfoYarnCount,
            props: {
              placeholder: 'Enter Yarn Count',
            },
          },
          {
            label: 'Fabric Weight and Construct (kg)',
            name: 'tcProductInfoFabricWeightAndConstruct',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: !addWithComponent && FABRIC_WEIGHT_CONSTRUCT_PC_CODES.includes(productCategoryCode),
            value: additionalInfoData.tcProductInfoFabricWeightAndConstruct,
            props: {
              placeholder: 'Enter Fabric Weight and Construct',
            },
          },
          {
            label: 'Fabric Weight of Finished Product (kg)',
            name: 'tcProductInfoFabricWeightOfFinishedProduct',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: !addWithComponent && (FABRIC_WEIGHT_FINISHED_PC_CODES.includes(productCategoryCode) || FABRIC_WEIGHT_FINISHED_PD_CODES.includes(productDetailCode)),
            value: additionalInfoData.tcProductInfoFabricWeightOfFinishedProduct,
            props: {
              placeholder: 'Enter Fabric Weight of Finished Product',
            },
          },
          {
            label: 'Trims Excluded',
            name: 'tcProductInfoTrimsExcluded',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            value: additionalInfoData.tcProductInfoTrimsExcluded,
            props: {
              placeholder: 'Enter Trims Excluded',
            },
          },
          {
            label: 'Additional Info',
            name: 'tcProductInfoAdditional',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            value: additionalInfoData.tcProductInfoAdditional,
            props: {
              placeholder: 'Enter Additional Info',
            },
          },
          {
            label: 'Product Additives',
            name: 'tcProductAdditives',
            type: 'chips',
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            value: additionalInfoData.tcProductAdditives,
            props: {
              placeholder: 'Enter Product Additives',
            },
          },
          ...(addWithComponent
            ? [
                {
                  label: 'Product Additives Weight (kg)',
                  name: 'tcProductAdditivesWeight',
                  type: 'number',
                  gridSize: { xs: 12, md: 4, lg: 4 },
                  required: false,
                  value: additionalInfoData.tcProductAdditivesWeight,
                  props: {
                    type: 'number',
                    placeholder: 'Enter Product Additives Weight',
                  },
                },
              ]
            : []),
        ],
      },
    ],
    handleSubmit: (values: any) => {
      setAdditionalInfoData(values);
      toggleDrawer();
    },
  };

  return (
    <>
      <FormBuilder formConfig={formConfig} onSubmit={formConfig.handleSubmit} formRef={formRef} />
    </>
  );
};

export default AdditionalInfo;
